import { useEffect, useRef, useState } from "react";
import Box from "../../../shared/Box";
import { useEmployees, IncrementHistory } from "../modal/EmployeesContext";
import IncrementModalForm from "./IncreamentModalForm";
import DeleteModal from "shared/DeleteModal";
import RowActionsMenu from "shared/RowActionsMenu";
import { Pencil, Trash2 } from "lucide-react";
import { deleteIncrement } from "../api/employeesApi";

interface IncrementHistoryTableProps {
    employeeCode: string;
    onReload: () => void;
}

const IncrementHistoryTable = ({ employeeCode, onReload }: IncrementHistoryTableProps) => {
    const { employeeIncreamentList, isDeleteModal, setIsDeleteModal } = useEmployees();
    const [updateModal, setUpdateModal] = useState<IncrementHistory | null>(null);
    const [deleteError, setDeleteError] = useState("");
    const deleteModalRef = useRef<HTMLDivElement>(null);

    const updateModalOpen = (incrementData: IncrementHistory) => {
        setUpdateModal(incrementData);
        window.scrollTo(0, 0);
        document.body.style.overflow = "hidden";
    };

    const updateModalClose = () => {
        setUpdateModal(null);
        window.scrollTo(0, 0);
        document.body.style.overflow = "auto";
    };

    const handleUpdateSuccess = () => {
        updateModalClose();
        onReload();
    };

    const deletingModal = (incrementData: IncrementHistory) => {
        setDeleteError("");
        setIsDeleteModal(incrementData);
        window.scrollTo(0, 0);
        document.body.style.overflow = "hidden";
    };

    const deleteModalClose = () => {
        setIsDeleteModal(null);
        window.scrollTo(0, 0);
        document.body.style.overflow = "auto";
    };

    const handleDelete = async (increment: IncrementHistory) => {
        try {
            await deleteIncrement(parseInt(increment.increamentId || "0"));
            deleteModalClose();
            onReload();
        } catch (err: any) {
            const detail = err.response?.data?.detail;
            setDeleteError(typeof detail === "string" ? detail : "Failed to delete increment");
        }
    };

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (deleteModalRef.current && !deleteModalRef.current.contains(event.target as Node)) {
                deleteModalClose();
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const tableHeadingClassName = "py-3 px-4 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider font-inter";
    const tableDataClassName = "py-4 px-4 text-sm text-foreground font-inter";

    return (
        <>
            <Box boxMainDivClasses="mt-[30px]" boxClass="bg-surface border-border">
                <div className="w-full overflowXAuto">
                    <table className="w-full">
                        <thead className="bg-surface-muted">
                            <tr className="border-b border-border">
                                <th className={`${tableHeadingClassName} w-[25%]`}>Amount</th>
                                <th className={`${tableHeadingClassName} w-[25%]`}>Effective Date</th>
                                <th className={`${tableHeadingClassName} w-[25%]`}>Notes</th>
                                <th className={`${tableHeadingClassName} w-[25%]`}>Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            {employeeIncreamentList.length === 0 ? (
                                <tr>
                                    <td colSpan={4} className="py-12 text-center text-muted-foreground text-sm font-inter">
                                        No increment history found
                                    </td>
                                </tr>
                            ) : (
                                employeeIncreamentList.map((data, index) => (
                                    <tr key={index} className="border-t border-border">
                                        <td className={tableDataClassName}>{data.increamentAmount}</td>
                                        <td className={tableDataClassName}>{data.increamentDate}</td>
                                        <td className={tableDataClassName}>{data.notes || "—"}</td>
                                        <td className={tableDataClassName}>
                                            <RowActionsMenu
                                                actions={[
                                                    { label: "Update", icon: Pencil, onClick: () => updateModalOpen(data) },
                                                    { label: "Delete", icon: Trash2, onClick: () => deletingModal(data), destructive: true },
                                                ]}
                                            />
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </Box>

            {updateModal && (
                <IncrementModalForm
                    employeeCode={employeeCode}
                    incrementFieldsData={updateModal}
                    onSuccess={handleUpdateSuccess}
                    closeModal={updateModalClose}
                />
            )}

            {isDeleteModal && (
                <DeleteModal ref={deleteModalRef} closeButtonCLick={deleteModalClose}>
                    <h1 className="text-lg text-center font-inter font-semibold text-foreground border-b border-border p-5 mb-6">Delete Increment</h1>
                    <div className="flex flex-col gap-3 px-5 mb-5">
                        <p className="text-sm font-inter text-foreground">
                            Amount: <span className="font-semibold text-foreground">{isDeleteModal.increamentAmount}</span>
                        </p>
                        <p className="text-sm font-inter text-foreground">
                            Date: <span className="font-semibold text-foreground">{isDeleteModal.increamentDate}</span>
                        </p>
                    </div>
                    {deleteError && (
                        <div className="mx-5 mb-4 p-3 bg-destructive/10 border border-destructive/20 rounded-xl">
                            <p className="text-destructive text-sm font-inter">{deleteError}</p>
                        </div>
                    )}
                    <div className="border-t border-border py-4 px-5 flex justify-end gap-3">
                        <button onClick={deleteModalClose} className="px-4 py-2 text-sm text-muted-foreground hover:text-foreground font-inter transition-colors" type="button">
                            Cancel
                        </button>
                        <button onClick={() => handleDelete(isDeleteModal)} className="px-4 py-2 text-sm bg-destructive hover:bg-destructive/90 text-destructive-foreground rounded-lg font-inter transition-colors" type="button">
                            Confirm Delete
                        </button>
                    </div>
                </DeleteModal>
            )}
        </>
    );
};

export default IncrementHistoryTable;
